/**
 * CheckpointsManagement - Manage patrol checkpoints
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  MapPin, Plus, Edit2, Trash2, Camera, QrCode, 
  Check, X, GripVertical, AlertTriangle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckpointsManagement = () => {
  const queryClient = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingCheckpoint, setEditingCheckpoint] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch checkpoints
  const { data: checkpoints = [], isLoading } = useQuery({
    queryKey: ['admin-checkpoints'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/checkpoints`, { headers });
      if (!res.ok) throw new Error('Failed to fetch checkpoints');
      return res.json();
    }
  });

  // Create checkpoint
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/checkpoints`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create checkpoint');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-checkpoints']);
      setShowAddDialog(false);
      toast.success('Checkpoint created');
    },
    onError: (error) => toast.error(error.message)
  });

  // Update checkpoint
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/api/admin/checkpoints/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update checkpoint');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-checkpoints']);
      setEditingCheckpoint(null);
      toast.success('Checkpoint updated');
    },
    onError: (error) => toast.error(error.message)
  });

  // Delete checkpoint
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/api/admin/checkpoints/${id}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to delete checkpoint');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-checkpoints']);
      setDeleteConfirm(null);
      toast.success('Checkpoint deleted');
    },
    onError: (error) => toast.error(error.message)
  });

  const CheckpointForm = ({ checkpoint, onSubmit, onCancel, isLoading }) => {
    const [formData, setFormData] = useState({
      name: checkpoint?.name || '',
      location: checkpoint?.location || '',
      description: checkpoint?.description || '',
      order: checkpoint?.order || 0,
      required_photo: checkpoint?.required_photo ?? true,
      qr_code: checkpoint?.qr_code || ''
    });

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(formData);
        }}
        className="space-y-4"
      >
        <div>
          <label className="text-sm font-medium">Checkpoint Name *</label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            placeholder="e.g., Main Gate"
            data-testid="input-checkpoint-name"
          />
        </div>

        <div>
          <label className="text-sm font-medium">Location *</label>
          <Input
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            required
            placeholder="e.g., Building A Entrance"
          />
        </div>

        <div>
          <label className="text-sm font-medium">Description</label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Additional details about this checkpoint"
            rows={2}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Order (in patrol)</label>
            <Input
              type="number"
              min="0"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div>
            <label className="text-sm font-medium">QR Code (auto-generated if empty)</label>
            <Input
              value={formData.qr_code}
              onChange={(e) => setFormData({ ...formData, qr_code: e.target.value })}
              placeholder="CP-XXXXXXXX"
            />
          </div>
        </div>

        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <p className="font-medium text-sm">Require Photo</p>
            <p className="text-xs text-gray-500">Officers must take a photo when checking in</p>
          </div>
          <Switch
            checked={formData.required_photo}
            onCheckedChange={(checked) => setFormData({ ...formData, required_photo: checked })}
          />
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={isLoading} data-testid="submit-checkpoint-btn">
            {isLoading ? 'Saving...' : checkpoint ? 'Update Checkpoint' : 'Create Checkpoint'}
          </Button>
        </div>
      </form>
    );
  };

  return (
    <div className="space-y-6" data-testid="checkpoints-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Patrol Checkpoints</h2>
          <p className="text-sm text-gray-500">Manage locations for security patrol rounds</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-checkpoint-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Checkpoint
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading checkpoints...</div>
      ) : checkpoints.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium mb-2">No checkpoints configured</h3>
            <p className="text-sm text-gray-500 mb-4">
              Add checkpoints for security officers to visit during patrols
            </p>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add First Checkpoint
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {checkpoints.sort((a, b) => a.order - b.order).map((checkpoint, index) => (
            <Card key={checkpoint.id} data-testid={`checkpoint-card-${checkpoint.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-medium">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium">{checkpoint.name}</h3>
                      {checkpoint.required_photo && (
                        <Badge variant="outline" className="text-xs">
                          <Camera className="h-3 w-3 mr-1" />
                          Photo Required
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{checkpoint.location}</p>
                    {checkpoint.description && (
                      <p className="text-xs text-gray-400 mt-1">{checkpoint.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="secondary" className="text-xs font-mono">
                        <QrCode className="h-3 w-3 mr-1" />
                        {checkpoint.qr_code}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => setEditingCheckpoint(checkpoint)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => setDeleteConfirm(checkpoint)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Checkpoint Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Checkpoint</DialogTitle>
          </DialogHeader>
          <CheckpointForm
            onSubmit={(data) => createMutation.mutate(data)}
            onCancel={() => setShowAddDialog(false)}
            isLoading={createMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Checkpoint Dialog */}
      <Dialog open={!!editingCheckpoint} onOpenChange={(open) => !open && setEditingCheckpoint(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Checkpoint</DialogTitle>
          </DialogHeader>
          {editingCheckpoint && (
            <CheckpointForm
              checkpoint={editingCheckpoint}
              onSubmit={(data) => updateMutation.mutate({ id: editingCheckpoint.id, data })}
              onCancel={() => setEditingCheckpoint(null)}
              isLoading={updateMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Delete Checkpoint
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirm?.name}"? This will remove it from all patrol routes.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={() => deleteMutation.mutate(deleteConfirm.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Checkpoint'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CheckpointsManagement;
